<template>
  <div>
    <DataTable
      :cell-props="{ style: 'padding-top: 16px; padding-bottom: 16px;' }"
      :headers="tableHeaders"
      :items="filteredAgreements"
      :loading="processing"
      class="border"
      no-data-text="No agreements assigned"
      hide-default-footer
    >
      <template #[`item.name`]="{ item }">
        <div class="d-flex align-center ga-2">
          <span>
            <span
              @click="openAgreement(item)"
              class="c-primary underlined pointer"
              data-cy="agreement-name"
              data-testid="agreement-name"
            >
              {{ item.name }}
            </span>
          </span>

          <TooltipIcon
            color="grey"
            icon="info"
            location="top"
            open-on-click
          >
            <div>
              <div v-if="item.sent_at">
                {{ $bct('Sent at') }}: <LongDateTime :date="item.sent_at" />
              </div>

              <div v-if="item.status === 'Partial' && item.signatures?.[0]?.signed_at">
                {{ $bct('Partially signed at') }}:
                <LongDateTime :date="item.signatures[0].signed_at" />
              </div>

              <div v-if="item.expires_at">
                {{ $bct('Expiration') }}:
                <LongDate :date="item.expires_at" />
              </div>

              <div>{{ $bct('ID') }}: {{ item.id }}</div>
            </div>
          </TooltipIcon>
        </div>
      </template>

      <template #[`item.signers`]="{ item }">
        <div
          v-for="signer in listOfSigners(item)"
          :key="signer"
        >
          {{ signer }}
        </div>
      </template>

      <template #[`item.status`]="{ item }">
        <div
          v-if="statusMessage(item)"
          data-cy="agreement-status"
        >
          <span class="d-flex align-center">
            <v-icon
              :color="statusMessage(item).color"
              class="material-icons-outlined"
              start
            >
              {{ statusMessage(item).icon }}
            </v-icon>

            <span data-testid="agreement-status-text">
              {{ $bct(statusMessage(item).text) }}
            </span>
          </span>
        </div>
      </template>

      <template #[`item.actions`]="{ item }">
        <div class="d-flex justify-end align-center ga-4">
          <StandardButton
            v-if="isSendable(item)"
            @click="requestAgreementSend(item)"
            :loading="processing"
            data-cy="agreement-send-button"
            data-testid="agreement-send-button"
            role="secondary"
            text="Send"
          />

          <StandardButton
            v-else-if="isNextSigner(item)"
            @click="openAgreement(item)"
            :loading="processing"
            data-testid="agreement-review-and-sign-button"
            role="secondary"
            text="Review and sign"
          />

          <ActionMenu
            v-if="role === 'organization'"
            @click:action:accept="acceptAgreement(item)"
            @click:action:delete="destroyAgreement(item)"
            @click:action:download="downloadAgreement(item)"
            @click:action:expire="
              updateAgreementExpiration({
                ...item,
                expires_at: new Date().toLocaleDateString('en-CA'),
              })
            "
            @click:action:preview="openAgreement(item)"
            @click:action:update_expiration="modifyExpirationDialog.open(item)"
            @click:action:void="voidAgreement(item)"
            :items="actionItems(item)"
            button-data-cy="agreement-action-button"
            button-icon="more_vert"
            button-test-id="agreement-action-menu"
            left
          />
        </div>
      </template>
    </DataTable>

    <AgreementDialog
      @change="emit('change')"
      ref="agreementDialog"
      :role="role"
    />

    <ResourceDialog
      @save="updateAgreementExpiration"
      ref="modifyExpirationDialog"
      :fields="[
        {
          text: 'Expiration date',
          description:
            'Choose a future date for the expiration of this agreement, or choose to expire this agreement now.',
          value: 'expires_at',
          type: 'date',
        },
      ]"
      :processing="processing"
      title="Modify agreement expiration"
      close-on-save
    />

    <ConfirmDialog ref="confirmDialog" />
  </div>
</template>

<script setup>
import _ from 'lodash';
import { useStore } from 'vuex';

import ActionMenu from '@/shared/components/ActionMenu.vue';
import AgreementDialog from '@/shared/components/agreement/AgreementDialog.vue';
import ConfirmDialog from '@/shared/components/ConfirmDialog.vue';
import DataTable from '@/shared/components/table/DataTable.vue';
import LongDate from '@/shared/components/LongDate.vue';
import LongDateTime from '@/shared/components/LongDateTime.vue';
import useApi from '@/shared/composables/useApi';
import useEventBus from '@/shared/composables/useEventBus';

import ResourceDialog from '../form/ResourceDialog.vue';
import TooltipIcon from '../TooltipIcon.vue';

const eventBus = useEventBus();
const store = useStore();
const { api: Api, scopedApi } = useApi();

const $bct = inject('translator');

const props = defineProps({
  agreements: {
    type: Array,
    default: () => [],
    required: true,
  },
  locked: {
    type: Boolean,
    default: false,
  },
  processing: Boolean,
  role: {
    type: String,
    required: true,
  },
  subsidyAwardV2: {
    type: Boolean,
    default: false,
  },
  subsidyAwardStatus: {
    type: String,
    default: null,
  },
});

const emit = defineEmits(['change', 'destroy', 'processing', 'open']);

const agreementDialog = ref(null);
const confirmDialog = ref(null);
const localSchemas = ref({});
const modifyExpirationDialog = ref(null);
const tableHeaders = ref([
  { title: 'Name', key: 'name', sortable: false },
  { title: 'Signers', key: 'signers', sortable: false },
  { title: 'Status', key: 'status', sortable: false },
  { title: '', value: 'actions', align: 'right', sortable: false },
]);

const filteredAgreements = computed(() => {
  if (props.role === 'organization') return props.agreements;

  return props.agreements.filter(
    (agreement) => agreement.status !== 'Expired' && agreement.status !== 'Canceled',
  );
});

const schemas = computed(() => {
  return {
    ...store.state.schemas,
    ...localSchemas.value,
  };
});

const isSubsidyAwardInEndState = computed(() => {
  return ['Awarded', 'Canceled', 'Closed'].includes(props.subsidyAwardStatus);
});

function acceptAgreement(agreement) {
  scopedApi.agreement.update(
    agreement.id,
    { status: 'Accepted' },
    {},
    () => {
      emit('change');
      eventBus.chime('Agreement accepted');
    },
    (error) => {
      eventBus.error(error);
    },
  );
}

function agreementSchema(agreement) {
  return schemas.value[agreement.schema_id];
}

function actionItems(agreement) {
  const actions = [];
  const viewAction = {
    avatar: 'file_open',
    event: 'preview',
    title: 'View',
  };
  const downloadAction = {
    avatar: 'file_download',
    event: 'download',
    title: 'Download',
  };
  const modifyExpirationAction = {
    avatar: 'hourglass_disabled',
    event: 'update_expiration',
    title: 'Modify expiration',
  };
  const voidAction = {
    avatar: 'cancel',
    event: 'void',
    title: 'Void',
  };
  const deleteAction = {
    avatar: 'delete',
    event: 'delete',
    title: 'Delete',
  };

  actions.push(viewAction);
  // We want to allow download of any fully signed agreement
  // submitted_at is usually not being set (as of 2026-03-05), but we can fall back to it when set
  // This logic will currently miss Void agreements that were previously signed
  if (agreement.status === 'Signed' || agreement.accepted_at || agreement.submitted_at)
    actions.push(downloadAction);

  // STOP here when the specialist locked prop is set)
  if (props.locked) return actions;

  if (agreement.status === 'Signed') {
    actions.push({ avatar: 'check', event: 'accept', title: 'Accept' });
  }
  // Modify expiration and Void are not available for a SubsidyAward that is in an end state (Awarded, Canceled, or Closed)
  if (!isSubsidyAwardInEndState.value) {
    if (!['Signed', 'Accepted', 'Canceled', 'Void'].includes(agreement.status)) {
      actions.push(modifyExpirationAction);
    }
    if (!['Canceled', 'Void'].includes(agreement.status)) {
      actions.push(voidAction);
    }
  }
  // Delete is not allowed on agreements that were ever sent
  if (!agreement.sent_at) {
    actions.push(deleteAction);
  }

  return actions;
}

function decodeSigner(signerRole, signer) {
  if (!signerRole || !signer) return null;

  let recipient = signerRole === 'Family' ? 'Parent' : signerRole;

  let recipientText = `${recipient} recipient`;
  if (signer.signatory_name) recipientText += ` (${signer.signatory_name})`;
  if (signer.signed_at) recipientText += ' ✓';

  return recipientText;
}

async function destroyAgreement(agreement) {
  const confirm = await confirmDialog.value.confirmWithText(
    'Are you sure you want to remove this agreement?',
  );
  if (!confirm) return;

  scopedApi.agreement.destroy(agreement.id, () => {
    emit('destroy');
  });
}

async function voidAgreement(agreement) {
  const confirm = await confirmDialog.value.confirmWithText(
    'Voiding this agreement will prevent any further signatures or edits. Do you want to continue?',
    { confirmButtonText: 'Continue' },
  );
  if (!confirm) return;

  emit('processing', true);
  const response = await scopedApi.agreement.update(agreement.id, { status: 'Void' });
  emit('processing', false);

  if (response?.status !== 200) return;

  emit('change');
  eventBus.chime('Agreement void');
}

function downloadAgreement(agreement) {
  const link = scopedApi.agreement.downloadUrl(agreement.id, agreement.name.replaceAll(' ', '-'));
  window.open(link);
}

function isNextSigner(agreement) {
  if (!['Sent', 'Partial'].includes(agreement.status)) return false;
  if (props.locked) return false;

  let nextSignatoryId;
  const orderedSignatures = _.orderBy(agreement.signatures, 'ordinal');

  for (const signature of orderedSignatures) {
    if (signature.signed_at) continue;

    nextSignatoryId = signature.signatory_id;
    break;
  }

  if (!nextSignatoryId) return false;

  return nextSignatoryId === store.state?.profile?.id;
}

function isSendable(agreement) {
  if (agreement.status !== 'Drafted') return false;
  if (agreement.sent_at) return false;
  if (props.role !== 'organization') return false;
  if (props.locked) return false;
  if (isSubsidyAwardInEndState.value) return false;

  return true;
}

async function load() {
  const missingSchemaIds = props.agreements
    .map((agreement) => agreement.schema_id)
    .filter((schemaId) => schemaId && !store.state.schemas[schemaId]);

  const uniqueSchemaIds = [...new Set(missingSchemaIds)];

  for (const schemaId of uniqueSchemaIds) {
    const response = await Api.publicApi.organization.schema.get(schemaId);
    if (!response?.data) {
      eventBus.chime('Failed to load schema for agreement');
      continue;
    }

    localSchemas.value[schemaId] = response.data;
  }
}

function listOfSigners(agreement) {
  const schema = agreementSchema(agreement);
  const meta = schema?.meta;
  const signers = [];

  if (!meta || !agreement.signatures) return signers;

  const signerField = decodeSigner(meta.signer_role, agreement.signatures[0]);
  if (signerField) signers.push(signerField);

  if (meta.second_signer_role) {
    const secondSignerField = decodeSigner(meta.second_signer_role, agreement.signatures[1]);
    if (secondSignerField) signers.push(secondSignerField);
  }

  return signers;
}

function openAgreement(agreement) {
  agreementDialog.value.open(agreement.id, { preview: true });
}

async function requestAgreementSend(agreement) {
  emit('processing', true);
  const response = await scopedApi.agreement.update(agreement.id, { send_requested: true });
  emit('processing', false);

  if (response?.status !== 200) return;

  emit('change');
  eventBus.chime('Sent');
}

function statusMessage(agreement) {
  const checkNextSigner = (agreement) => {
    if (isNextSigner(agreement))
      return { icon: 'mark_email_unread', color: 'warning', text: 'Ready to sign' };

    return { icon: 'hourglass_empty', color: 'info', text: 'Awaiting another signature' };
  };

  const statusMap = {
    organization: {
      Drafted: { icon: 'drafts', color: 'error', text: 'Not sent' },
      Sent: { icon: 'email', color: 'info', text: 'Sent' },
      Signed: { icon: 'check', color: 'success', text: 'Signed' },
      Accepted: { icon: 'check_circle', color: 'success', text: 'Signed and accepted' },
      Denied: { icon: 'cancel', color: 'error', text: 'Denied' },
      Expired: { icon: 'cancel', color: 'grey', text: 'Expired' },
      Void: { icon: 'cancel', color: 'grey', text: 'Void' },
      Partial: { icon: 'hourglass_empty', color: 'warning', text: 'Partially completed' },
      Canceled: { icon: 'cancel', color: 'grey', text: 'Canceled' },
    },
    manager: {
      // Drafted should not be visible to managers
      Sent: checkNextSigner(agreement),
      Signed: { icon: 'check', color: 'success', text: 'Signed' },
      Accepted: { icon: 'check_circle', color: 'success', text: 'Signed and accepted' },
      Denied: { icon: 'cancel', color: 'error', text: 'Denied' },
      Expired: { icon: 'cancel', color: 'grey', text: 'Expired' },
      Void: { icon: 'cancel', color: 'grey', text: 'Void' },
      Partial: checkNextSigner(agreement),
      // Cancelled should not be visible to managers
    },
    parent: {
      // Drafted should not be visible to parents
      Sent: checkNextSigner(agreement),
      Signed: { icon: 'check', color: 'success', text: 'Signed' },
      Accepted: { icon: 'check_circle', color: 'success', text: 'Signed and accepted' },
      Denied: { icon: 'cancel', color: 'error', text: 'Denied' },
      Expired: { icon: 'cancel', color: 'grey', text: 'Expired' },
      Void: { icon: 'cancel', color: 'grey', text: 'Void' },
      Partial: checkNextSigner(agreement),
      // Cancelled should not be visible to parents
    },
  };

  return statusMap[props.role]?.[agreement.status] || null;
}

async function updateAgreementExpiration(agreement) {
  emit('processing', true);
  const response = await scopedApi.agreement.update(agreement.id, {
    expires_at: agreement.expires_at,
  });
  emit('processing', false);

  if (response.status !== 200) return;

  emit('change');
  eventBus.chime('Updated');
}

onMounted(load);
</script>
